import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@cad-arc';
import { takeLast } from '../common/helpers/take-last-promise';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EsriLocationMap } from '../location-select/components/location-map/esri-location-map';
import { MapboxLocationMap } from '../location-select/components/location-map/mapbox-location-map';
import { LocationDisplayProps } from '../location-select/components/location-map/types';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { LocationStoreView } from '../location-select/types/locations';
import { CommandLineEditorStore } from './command-line-editor-store';

export const LocationSearchHelper = observer(function LocationSearchHelper(props: {
    editorStore: CommandLineEditorStore;
    locationApi: ReturnType<typeof useLocationApi>;
}): React.ReactElement {
    const ensureLast = useRef(takeLast<LocationStoreView[]>());
    const { editorStore } = props;
    const query = editorStore.helperSearchText;
    const locationStore = editorStore.locationStore;
    const ref = useRef<Element>(null);

    const selectedIndex = editorStore.helperState?.selectedIndex;

    const { searchLocations } = props.locationApi;
    const { useMapboxMapForLocationInputs } = useEnabledFeatures();

    const debouncedSearch = useMemo(() => {
        async function onSearch(query: string): Promise<void> {
            if (query === '') {
                editorStore.setHelperOptions([]);
                locationStore.setItems([]);
            } else {
                const newResults = await ensureLast.current(searchLocations(query));
                locationStore.setItems(newResults);
                if (newResults.length) {
                    locationStore.setHighlightedLocationIndex(0, { source: 'keyboard' });
                    editorStore.setHelperSelectedIndex(0);
                }
            }
        }

        return debounce((query: string) => onSearch(query), 300);
    }, [locationStore, searchLocations, editorStore]);

    function onOpenPopover(): void {
        locationStore.setSort(undefined);
    }

    useEffect(() => {
        if (query) {
            debouncedSearch(query);
        }
    }, [query]); // eslint-disable-line

    useEffect(() => {
        onOpenPopover();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (typeof selectedIndex !== 'undefined') {
            locationStore.setHighlightedLocationIndex(selectedIndex, { source: 'keyboard' });
        }
    }, [locationStore, selectedIndex]);

    const onClickRow = useCallback(
        (id: number) => {
            const location = locationStore.renderedLocationData.renderedList[id];

            if (location) {
                editorStore.selectLocation(location, true);
            } else if (id === -1) {
                // Mimic tabbing functionality to extract free text location.
                editorStore.tabOutOfCurrentSegment(true);
            }
        },
        [locationStore, editorStore]
    );

    const locationDisplayProps: LocationDisplayProps = {
        store: locationStore,
        isLoadingResults: false,
        query: query ?? '',
        onClickRow,
        showLocationDetails: true,
        showMapKey: false,
        isCliMode: true,
    };

    return (
        <Box
            ref={ref}
            className="cli-location-helper"
            bg="surface.canvas"
            position="relative"
            transform="translateX(-50%)"
            left="50%"
            w="92%"
            border="md"
            borderColor="var(--cad-colors-command-line-popout-border)"
            borderRadius="base"
        >
            {useMapboxMapForLocationInputs ? (
                <MapboxLocationMap {...locationDisplayProps} />
            ) : (
                <EsriLocationMap {...locationDisplayProps} />
            )}
        </Box>
    );
});
